/**
 * 💝 격려 받음 모달
 * 새로운 격려 메시지를 받았을 때 일회성으로 표시되는 알림 모달
 */

import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Heart } from 'lucide-react';
import { triggerHappyMoment } from '../utils/confetti';

export default function CheerReceivedModal({ cheer, onClose, onMarkAsRead }) {
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        if (cheer) {
            // 모달 등장 애니메이션
            setTimeout(() => setIsVisible(true), 50);
            // 컨페티 효과
            triggerHappyMoment();
        }
    }, [cheer]);

    if (!cheer) return null;

    // 날짜/시간 포맷팅
    const formatDateTime = (timestamp) => {
        if (!timestamp) return '';
        const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
        const now = new Date();
        const diffMs = now - date;
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);

        if (diffMins < 1) return '방금 전';
        if (diffMins < 60) return `${diffMins}분 전`;
        if (diffHours < 24) return `${diffHours}시간 전`;
        if (diffDays < 7) return `${diffDays}일 전`;

        // 7일 이상이면 날짜 표시
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const mins = String(date.getMinutes()).padStart(2, '0');

        return `${year}.${month}.${day} ${hours}:${mins}`;
    };

    const handleClose = () => {
        setIsVisible(false);
        setTimeout(() => {
            if (onMarkAsRead) onMarkAsRead(cheer.id);
            if (onClose) onClose();
        }, 200);
    };

    return createPortal(
        <div
            className={`fixed inset-0 z-[10000] flex items-center justify-center p-4 transition-all duration-300 ${isVisible ? 'bg-black/30' : 'bg-transparent'}`}
            onClick={handleClose}
        >
            <div
                className={`relative bg-white rounded-[2rem] shadow-2xl w-full max-w-sm overflow-hidden transform transition-all duration-300 ${isVisible ? 'scale-100 opacity-100' : 'scale-90 opacity-0'}`}
                onClick={e => e.stopPropagation()}
            >
                {/* 배경 그라데이션 */}
                <div className="absolute inset-0 bg-gradient-to-br from-rose-100 via-pink-50 to-white opacity-70" />

                {/* 장식용 도형 */}
                <div className="absolute top-[-30%] right-[-20%] w-40 h-40 rounded-full bg-rose-200/30 blur-3xl" />
                <div className="absolute bottom-[-20%] left-[-15%] w-32 h-32 rounded-full bg-pink-200/30 blur-2xl" />

                {/* 닫기 버튼 */}
                <button
                    onClick={handleClose}
                    className="absolute top-4 right-4 z-20 p-2 bg-white/50 hover:bg-white rounded-full text-gray-400 hover:text-gray-600 transition"
                >
                    <X size={18} />
                </button>

                {/* 콘텐츠 */}
                <div className="relative z-10 px-8 py-10 text-center">
                    {/* 아이콘 */}
                    <div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-br from-rose-400 to-pink-500 rounded-full flex items-center justify-center shadow-lg shadow-rose-500/30 animate-bounce-subtle">
                        <Heart size={40} className="text-white fill-white" />
                    </div>

                    {/* 제목 */}
                    <h2 className="text-2xl font-black text-gray-800 mb-2">
                        격려가 도착했어요! 💝
                    </h2>

                    {/* 보낸 사람 */}
                    <p className="text-gray-500 mb-6">
                        <span className="font-bold text-rose-600">{cheer.senderName || '익명'}</span>님이 보냈어요
                    </p>

                    {/* 메시지 카드 */}
                    <div className="bg-white rounded-2xl p-6 shadow-lg border border-rose-100 mb-6">
                        <div className="flex items-center justify-center gap-2 mb-3">
                            {cheer.emoji && (
                                <span className="text-3xl">{cheer.emoji}</span>
                            )}
                        </div>
                        <p className="text-xl font-bold text-gray-800 leading-relaxed">
                            {cheer.message}
                        </p>
                    </div>

                    {/* 시간 */}
                    <p className="text-sm text-gray-400 mb-6">
                        {formatDateTime(cheer.createdAt)}
                    </p>

                    {/* 확인 버튼 */}
                    <button
                        onClick={handleClose}
                        className="w-full py-4 rounded-2xl bg-gradient-to-r from-rose-500 to-pink-500 text-white font-bold text-lg shadow-lg shadow-rose-500/30 hover:shadow-xl hover:shadow-rose-500/40 hover:-translate-y-0.5 active:scale-95 transition-all"
                    >
                        감사합니다! 🙏
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
}
